import { useEffect, useMemo, useState } from 'react'
import { getMyTeams } from 'API/team'
import { EMembershipStatus } from 'constants/membership'
import { TeamListItem } from 'types/auth'

// Organizations and their seasonal Teams, for the dashboard banner switcher.
//
// Sourced from GET /users/me/teams, which already fans a membership out into one
// row per Team across every organization the user belongs to — the whole tree in
// one request. GET /teams cannot serve this: it scopes to the globally active
// organization via the request interceptor, whereas the switcher lets a user look
// at an organization without switching the whole app into it.

export interface SwitcherOrg {
  id: number
  name: string
  logoUrl?: string
  primaryColor?: string
  /** Union of the sports across this org's teams — the banner label when no team is picked. */
  sports: string[]
  /**
   * Members of the organization, as the switch-organization modal already reports
   * it. Carried through from the same rows rather than fetched: every consumer of
   * this tree would otherwise ask for a number the response already contains.
   */
  memberCount?: number
}

export interface SwitcherTeam {
  id: number
  name: string
  sports: string[]
}

export interface OrgTeamTree {
  organizations: SwitcherOrg[]
  teamsByOrg: Map<number, SwitcherTeam[]>
  isLoading: boolean
}

const EMPTY_TREE: OrgTeamTree = {
  organizations: [],
  teamsByOrg: new Map(),
  isLoading: false
}

/**
 * Groups the flat row list into orgs and their teams.
 *
 * Invite-pending rows are dropped: an invitation is not a place you can view a
 * dashboard for. Rows without a teamId are kept for the organization they name —
 * that happens when every team of an org is archived, and dropping it would hide
 * an organization the user is still a member of.
 */
const buildTree = (rows: TeamListItem[]): Pick<OrgTeamTree, 'organizations' | 'teamsByOrg'> => {
  const joined = rows.filter(r => r.status !== EMembershipStatus.PENDING)

  const orgById = new Map<number, SwitcherOrg>()
  const teamsByOrg = new Map<number, SwitcherTeam[]>()

  for (const row of joined) {
    if (!orgById.has(row.orgId)) {
      orgById.set(row.orgId, {
        id: row.orgId,
        name: row.orgName,
        logoUrl: row.orgLogo,
        primaryColor: row.orgPrimaryColor,
        sports: [],
        memberCount: row.memberCount
      })
    }

    if (typeof row.teamId !== 'number') continue

    const bucket = teamsByOrg.get(row.orgId) ?? []
    // One membership can yield the same team twice if the row list ever repeats;
    // keep the first and move on rather than rendering a duplicate option.
    if (bucket.some(t => t.id === row.teamId)) continue
    bucket.push({ id: row.teamId, name: row.teamName ?? '', sports: row.sports ?? [] })
    teamsByOrg.set(row.orgId, bucket)
  }

  // Org-level sports = what its teams play, de-duplicated, order preserved.
  for (const [orgId, teams] of teamsByOrg) {
    const org = orgById.get(orgId)
    if (!org) continue
    org.sports = [...new Set(teams.flatMap(t => t.sports))]
  }

  return { organizations: [...orgById.values()], teamsByOrg }
}

/**
 * Session cache for the row list.
 *
 * The organizations a user belongs to and the Teams inside them do not change
 * while they browse, but the context banner now rides on every list page — so a
 * per-mount fetch meant one request per navigation. Cached at module scope so the
 * first page pays for it and the rest read it, and shared as a promise so two
 * banners mounting together still make one request.
 *
 * Invalidate after anything that adds or removes a Team or an Organization, and
 * after a profile switch — the response is scoped to the *active profile*, so a
 * coach and an athlete on the same account are answered different trees.
 */
let cachedRows: TeamListItem[] | null = null
let inflight: Promise<TeamListItem[]> | null = null

// Mounted consumers, so invalidation reaches the ones already on screen. Dropping
// the module cache alone only helps the next mount: a banner sitting on the page
// through a profile switch would keep rendering the tree it read at mount.
const subscribers = new Set<() => void>()

const loadRows = (): Promise<TeamListItem[]> => {
  if (cachedRows) return Promise.resolve(cachedRows)
  if (!inflight) {
    inflight = getMyTeams()
      .then(result => {
        cachedRows = result
        return result
      })
      .catch(() => {
        // A failed switcher must not take the page down with it — the banner falls
        // back to its empty state and everything below keeps rendering. Not cached,
        // so the next mount retries rather than inheriting the failure.
        return []
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/** Drop the cache and re-read it into every consumer currently on screen. */
export const invalidateOrgTeamTree = (): void => {
  cachedRows = null
  inflight = null
  subscribers.forEach(refetch => refetch())
}

export const useOrgTeamTree = (): OrgTeamTree => {
  const [rows, setRows] = useState<TeamListItem[]>(cachedRows ?? [])
  const [isLoading, setIsLoading] = useState<boolean>(cachedRows === null)

  useEffect(() => {
    let cancelled = false

    const read = () => {
      setIsLoading(cachedRows === null)
      void loadRows().then(result => {
        if (cancelled) return
        setRows(result)
        setIsLoading(false)
      })
    }

    read()
    subscribers.add(read)
    return () => {
      cancelled = true
      subscribers.delete(read)
    }
  }, [])

  const tree = useMemo(() => buildTree(rows), [rows])

  return rows.length === 0 && isLoading ? { ...EMPTY_TREE, isLoading: true } : { ...tree, isLoading }
}
